import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearch } from "wouter";
import { api, type Me } from "../api";
import { tracksFor, type Track, type TourStep } from "./tracks";

/**
 * First-run welcome tour. Mounted inside the app shell (above every page), so
 * client-side navigation never unmounts it: each step just navigates to its
 * real page and highlights a real element tagged `data-tour="…"`.
 * "Seen" is stored per user on the server (`tour_seen_at`), so it follows the
 * person across devices. It reopens from Your account → Replay the welcome tour.
 */

interface TourApi {
  start: (trackId?: string) => void;
}
const TourContext = createContext<TourApi>({ start: () => {} });
export const useTour = () => useContext(TourContext);

export function TourProvider({ me, onSeen, children }: { me: Me; onSeen: () => void; children: ReactNode }) {
  const tracks = tracksFor(me.role);
  const [state, setState] = useState<{ track: Track; index: number } | null>(null);
  const autoOpened = useRef(false);
  // Where focus goes back to when the tour closes (the control that opened it).
  const opener = useRef<HTMLElement | null>(null);

  const start = useCallback(
    (trackId = "main") => {
      const track = tracks.find((t) => t.id === trackId) ?? tracks[0]!;
      setState((prev) => {
        if (!prev) opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        return { track, index: 0 };
      });
    },
    [tracks],
  );

  // First visit: open automatically, once.
  useEffect(() => {
    if (!me.tourSeenAt && !autoOpened.current) {
      autoOpened.current = true;
      start("main");
    }
  }, [me.tourSeenAt, start]);

  const close = useCallback(() => {
    setState(null);
    // Back to the control that opened it; if the tour navigated away from it, to the page content.
    requestAnimationFrame(() => {
      if (opener.current?.isConnected) return opener.current.focus();
      document.getElementById("main")?.focus();
    });
    if (!me.tourSeenAt) {
      api("/auth/me", { method: "PATCH", json: { tourSeen: true } })
        .then(onSeen)
        .catch(() => null);
    }
  }, [me.tourSeenAt, onSeen]);

  return (
    <TourContext.Provider value={{ start }}>
      {children}
      {state && (
        <TourOverlay
          key={state.track.id}
          track={state.track}
          index={state.index}
          tracks={tracks}
          onIndex={(index) => setState({ track: state.track, index })}
          onStart={start}
          onClose={close}
        />
      )}
    </TourContext.Provider>
  );
}

function TourOverlay({
  track,
  index,
  tracks,
  onIndex,
  onStart,
  onClose,
}: {
  track: Track;
  index: number;
  tracks: Track[];
  onIndex: (i: number) => void;
  onStart: (trackId: string) => void;
  onClose: () => void;
}) {
  const step: TourStep = track.steps[index]!;
  const isLast = index === track.steps.length - 1;
  const [location, navigate] = useLocation();
  const search = useSearch();
  const cardRef = useRef<HTMLDivElement>(null);

  // Keyboard: focus moves into the card; Escape closes (the provider restores focus).
  // (Runs once per mount; a ref keeps the latest onClose without re-focusing on every render.)
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    cardRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Room below the last element on a page so it can scroll clear of the card.
  useEffect(() => {
    document.body.classList.add("tour-open");
    return () => document.body.classList.remove("tour-open");
  }, []);

  // Real navigation: when a step names a page, go there (Back and Next alike).
  // Runs on step change only, so it never fights the reader.
  useEffect(() => {
    let live = true;
    const target = step.path;
    if (!target) return;
    Promise.resolve(typeof target === "function" ? target() : target).then((path) => {
      if (live && path && path !== `${location}${search ? `?${search}` : ""}`) navigate(path);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.id, index]);

  const rect = useTargetRect(step.target, `${location}?${search}`);

  // Portaled to <body>, outside #root, so the target watcher never sees the tour's own updates.
  return createPortal(
    <>
      <div className="tour-blocker" aria-hidden="true" onClick={(e) => e.stopPropagation()}>
        {rect ? (
          <div className="tour-ring" style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }} />
        ) : (
          <div className="tour-dim" />
        )}
      </div>
      <div className="tour-anchor">
        <div ref={cardRef} className="tour-card" role="dialog" aria-modal="true" aria-labelledby="tour-title" tabIndex={-1}>
          <div className="tour-head">
            <span className="eyebrow">{track.label}</span>
            <button className="tour-close" onClick={onClose} aria-label="Close the tour">
              ×
            </button>
          </div>
          <div className="tour-body">
            <h2 id="tour-title">{step.title}</h2>
            {step.body}
            {isLast && <WrapUpChoices track={track} tracks={tracks} onStart={onStart} />}
          </div>
          <div className="tour-actions">
            <span className="tour-count" aria-live="polite">
              {index + 1} of {track.steps.length}
            </span>
            <button className="btn btn-small" onClick={() => onIndex(index - 1)} disabled={index === 0}>
              Back
            </button>
            {isLast ? (
              <button className="btn btn-small btn-primary" onClick={onClose}>
                Done
              </button>
            ) : (
              <button className="btn btn-small btn-primary" onClick={() => onIndex(index + 1)}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Wrap-up: replay the main tour, or jump to another walkthrough (never this one). */
function WrapUpChoices({ track, tracks, onStart }: { track: Track; tracks: Track[]; onStart: (id: string) => void }) {
  const others = tracks.filter((t) => t.id !== track.id && t.id !== "main");
  return (
    <div className="tour-choices">
      {track.id !== "main" && (
        <button className="btn btn-small" onClick={() => onStart("main")}>
          Replay the full tour
        </button>
      )}
      {others.map((t) => (
        <button key={t.id} className="btn btn-small" onClick={() => onStart(t.id)}>
          Show me: {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The card never moves, so the page does: bring the target into view and, if
 * it would sit under the card, scroll until it clears the card's top edge
 * (keeping the target's top below the sticky header).
 */
function scrollClearOfCard(r: DOMRect) {
  const HEADER = 96;
  const card = document.querySelector(".tour-card")?.getBoundingClientRect();
  // Anything in the card's columns must end above it, wherever it starts out.
  const sharesColumns = card && r.right > card.left && r.left < card.right;
  const limit = sharesColumns ? card.top - 16 : window.innerHeight - 16;
  if (r.top >= HEADER && r.bottom <= limit) return;
  const wantedTop = Math.max(HEADER, limit - r.height);
  // Instant, not smooth: each step is already a jump (often to a new page), and
  // a smooth scroll can stall when the tab isn't being painted.
  window.scrollBy({ top: r.top - wantedTop, behavior: "instant" });
}

/**
 * Tracks the on-screen box of `[data-tour="target"]`. Re-looks on every step and
 * route change, and keeps watching the DOM until the element appears (the
 * destination page may still be loading its data), then follows scroll/resize.
 */
function useTargetRect(target: string | undefined, routeKey: string): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    setRect(null);
    if (!target) return;
    let el: HTMLElement | null = null;
    let scrolled = false;
    const measure = () => {
      if (!el || !el.isConnected) el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
      if (!el) return setRect(null);
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return setRect(null);
      if (!scrolled) {
        scrolled = true;
        scrollClearOfCard(r);
      }
      const next = el.getBoundingClientRect();
      // Skip identical measurements so scroll/mutation storms don't re-render.
      setRect((prev) =>
        prev && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height ? prev : next,
      );
    };
    measure();
    const observer = new MutationObserver(measure);
    observer.observe(document.getElementById("root") ?? document.body, { childList: true, subtree: true });
    // A resize (or phone rotation) moves the card, so re-check the scroll too.
    const onResize = () => {
      scrolled = false;
      measure();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", measure, true);
    };
  }, [target, routeKey]);

  return rect;
}
